/**
 * Profile Detail — pure view.
 *
 * Elm-ish doctrine: no business logic, no useState for domain state,
 * no API calls. Renders based on `model` and dispatches via callbacks.
 *
 * Per §38 / §69 of the Bucket-B briefing, this surface is
 * `PROFILE_DETAIL_FIGMA = DESIGN_MISSING_CURRENT` (no live Figma
 * frame exists for other-user profile detail). The view therefore
 * uses existing design-system primitives rather than inventing a
 * bespoke new visual language.
 *
 * R1+R2+R3 (correction ACT): the public role label is rendered
 * from the OWNER's gender + goal (per PC-005 / PC-007 /
 * CONTRACT-001.3), not from any viewer's identity. The view does
 * NOT take a viewerGender prop.
 *
 * The Message CTA visibility is driven by `model.messageCtaEnabled`
 * (which the reducer owns; the harness supplies its environment-
 * derived value via `CtaEnabledChanged`). The view never inspects
 * viewer identity.
 */

import type { ReactNode } from 'react';
import type { ProfileDetailModel, ProfileDetail } from './model';
import {
  discoveryLabelFor,
  type InternalScenario,
  type InternalGender,
} from '@/features/profile-questionnaire/rolePresentation';
import { MessageCtaButton } from './components/MessageCtaButton';
import { NoPhotoFallback } from './components/NoPhotoFallback';
import './view.css';

export interface ProfileDetailViewProps {
  readonly model: ProfileDetailModel;
  readonly onRetry: () => void;
  readonly onMessageCtaClick: () => void;
  readonly onClearMessageCtaError: () => void;
}

function ownerScenario(detail: ProfileDetail): InternalScenario | null {
  if (detail.profile_type === 'sponsor') {
    return 'sponsor';
  }
  if (detail.goal === 'equal_seeker') return 'equal';
  if (detail.goal === 'no_work' || detail.goal === 'companion_seeker') {
    return 'no_work';
  }
  if (detail.goal === 'wealthy_seeker') return 'sponsor';
  return null;
}

function publicRoleLabel(detail: ProfileDetail): string {
  const scenario = ownerScenario(detail);
  const ownerGender: InternalGender | null =
    detail.gender === 'male' || detail.gender === 'female' ? detail.gender : null;
  if (scenario !== null && ownerGender !== null) {
    return discoveryLabelFor(scenario, ownerGender);
  }
  return detail.profile_type === 'sponsor' ? 'Спонсор' : 'Друг';
}

export function ProfileDetailView(props: ProfileDetailViewProps): ReactNode {
  const { model } = props;
  if (model.status === 'idle' || model.status === 'loading') {
    return (
      <div className="pd-loading" data-testid="profile-detail-loading">
        <p>Загрузка...</p>
      </div>
    );
  }
  if (model.status === 'error' || !model.detail) {
    return (
      <div className="pd-error" role="alert" data-testid="profile-detail-error">
        <p>Не удалось загрузить анкету</p>
        <button type="button" className="pd-btn-secondary" onClick={props.onRetry}>
          Попробовать снова
        </button>
      </div>
    );
  }
  return <ReadyView {...props} />;
}

function ReadyView(props: ProfileDetailViewProps): ReactNode {
  const { model } = props;
  const detail = model.detail;
  if (!detail) return null;

  const publicLabel = publicRoleLabel(detail);
  const ctaVisible = model.messageCtaEnabled;
  const ctaDisabled = model.messageCtaLoading;

  // CORRECTION03 (B-2.B): pick the canonical first-by-position photo
  // (server-canonical order; do NOT re-sort — the server is the
  // authority). When the profile has no photos, render the
  // existing NoPhotoFallback. The image src is the role-neutral
  // content URL consumed verbatim from the server response.
  const sortedPhotos = [...detail.photos].sort((a, b) => {
    const ap = typeof a.position === 'number' ? a.position : 0;
    const bp = typeof b.position === 'number' ? b.position : 0;
    return ap - bp;
  });
  const primaryPhoto = sortedPhotos[0] ?? null;

  return (
    <article
      className="pd-card"
      data-testid="profile-detail-card"
      data-variant={detail.profile_type}
    >
      <header className="pd-header">
        {primaryPhoto ? (
          <img
            className="pd-photo"
            data-testid="profile-detail-photo"
            data-photo-id={primaryPhoto.id}
            src={primaryPhoto.url}
            alt={detail.display_name ?? 'Аноним'}
            width={96}
            height={96}
          />
        ) : (
          <span data-testid="profile-detail-photo-fallback">
            <NoPhotoFallback name={detail.display_name ?? '?'} size={96} />
          </span>
        )}
        <div className="pd-identity">
          <h1 className="pd-name">{detail.display_name ?? 'Аноним'}</h1>
          {detail.city_or_region && (
            <p className="pd-city" data-testid="profile-detail-city">
              {detail.city_or_region}
            </p>
          )}
          <p className="pd-role" data-testid="profile-detail-role">
            {publicLabel}
          </p>
        </div>
      </header>

      {model.photoProtocolViolations.length > 0 && (
        <div
          className="pd-error"
          role="alert"
          data-testid="profile-detail-photo-violations"
        >
          <p>
            Некоторые фото профиля не удалось отобразить:&nbsp;
            {model.photoProtocolViolations.join('; ')}
          </p>
        </div>
      )}

      {detail.headline && (
        <section className="pd-section" data-testid="profile-detail-headline-section">
          <h2 className="pd-h2">Заголовок</h2>
          <p>{detail.headline}</p>
        </section>
      )}

      {detail.bio && (
        <section className="pd-section" data-testid="profile-detail-bio-section">
          <h2 className="pd-h2">О себе</h2>
          <p>{detail.bio}</p>
        </section>
      )}

      {model.messageCtaError && (
        <div className="pd-error" role="alert" data-testid="profile-detail-message-cta-error">
          <p>{model.messageCtaError}</p>
          <button
            type="button"
            className="pd-btn-secondary"
            onClick={props.onClearMessageCtaError}
          >
            Ок
          </button>
        </div>
      )}

      {ctaVisible && (
        <div className="pd-actions">
          <MessageCtaButton
            onClick={props.onMessageCtaClick}
            disabled={ctaDisabled}
          />
        </div>
      )}
    </article>
  );
}
